// Comms Brief / Work Order editor (/program-documents/:id). One schema-driven form: the backend
// sends the section schema (app/documents/schema.py) plus the content and, per field, where an
// auto-filled value came from, so this page has no per-document markup. Edits save as a Draft;
// Mark Final locks it; Word and PDF are rendered by the backend from the saved copy.
// Backend: /program-documents. See backend app/routers/CLAUDE.md.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import DocumentPreview from '../components/DocumentPreview';
import Section from '../components/Section';
import Toast from '../components/Toast';
import { downloadFileBlob } from '../utils/ciqAssets';
import {
  DOC_TYPE_LABELS, blankItem, confirmCounts, exportFileName, getValue, groupHeading, isDirty,
  linesToText, needsConfirm, setValue, sourceLabel, summarizeImport, textToLines,
} from '../utils/programDocuments';

const API = process.env.REACT_APP_API_URL;

const INPUT = {
  width: '100%', boxSizing: 'border-box', padding: '6px 8px', border: '1px solid #ccd4dd',
  borderRadius: '4px', fontSize: '13px', fontFamily: 'inherit',
};
const btn = (bg, disabled) => ({
  padding: '7px 14px', background: disabled ? '#bdc3c7' : bg, color: '#fff', border: 'none',
  borderRadius: '4px', cursor: disabled ? 'default' : 'pointer', fontSize: '13px', fontWeight: 600,
});
const smallBtn = (color) => ({
  padding: '3px 10px', background: 'transparent', color, border: `1px solid ${color}`,
  borderRadius: '4px', cursor: 'pointer', fontSize: '12px',
});

const errorText = (e) => (e?.response?.data?.detail && typeof e.response.data.detail === 'string')
  ? e.response.data.detail : (e?.message || 'Something went wrong');

function SourceChip({ source }) {
  const text = sourceLabel(source);
  if (!text) return null;
  const confirm = needsConfirm(source);
  return (
    <span title={confirm ? 'Suggested wording. Check it and edit if needed.' : text}
      style={{
        marginLeft: '8px', fontSize: '11px', fontWeight: 600, padding: '1px 7px', borderRadius: '10px',
        background: confirm ? '#fdf2e0' : source === 'typed' ? '#e8f0fb' : '#eef2f5',
        color: confirm ? '#b9770e' : source === 'typed' ? '#2e6bb5' : '#5d6d7e',
      }}>
      {text}
    </span>
  );
}

function FieldEditor({ field, value, onChange, source, disabled }) {
  const header = (
    <div style={{ fontSize: '12px', fontWeight: 600, color: '#2c3e50', marginBottom: '3px' }}>
      {field.label}<SourceChip source={source} />
    </div>
  );

  if (field.kind === 'text') {
    return (
      <div style={{ marginBottom: '12px' }}>
        {header}
        <input value={value ?? ''} disabled={disabled} style={INPUT} onChange={e => onChange(e.target.value)} />
      </div>
    );
  }

  if (field.kind === 'textarea' || field.kind === 'lines') {
    const text = field.kind === 'lines' ? linesToText(value) : (value ?? '');
    const rows = Math.max(2, Math.min(12, text.split('\n').length + 1));
    return (
      <div style={{ marginBottom: '12px' }}>
        {header}
        <textarea value={text} rows={rows} disabled={disabled} style={{ ...INPUT, resize: 'vertical' }}
          onChange={e => onChange(field.kind === 'lines' ? textToLines(e.target.value) : e.target.value)} />
        {field.kind === 'lines' && <div style={{ fontSize: '11px', color: '#97a0ad' }}>One item per line.</div>}
      </div>
    );
  }

  if (field.kind === 'table') {
    const rows = Array.isArray(value) ? value : [];
    const blankRow = () => Object.fromEntries(field.columns.map(c => [c.key, '']));
    const setCell = (i, key, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [key]: v } : r)));
    return (
      <div style={{ marginBottom: '12px' }}>
        {header}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                {field.columns.map(c => (
                  <th key={c.key} style={{ textAlign: 'left', fontSize: '11px', color: '#757575', padding: '2px 4px' }}>{c.label}</th>
                ))}
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  {field.columns.map(c => (
                    <td key={c.key} style={{ padding: '2px 4px' }}>
                      <input value={r[c.key] ?? ''} disabled={disabled} style={INPUT}
                        onChange={e => setCell(i, c.key, e.target.value)} />
                    </td>
                  ))}
                  <td style={{ padding: '2px 4px', whiteSpace: 'nowrap' }}>
                    {!disabled && <button type="button" style={smallBtn('#c0392b')} onClick={() => onChange(rows.filter((_, j) => j !== i))}>Remove</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!disabled && (
          <button type="button" style={{ ...smallBtn('#2e6bb5'), marginTop: '4px' }} onClick={() => onChange([...rows, blankRow()])}>
            + Add row
          </button>
        )}
      </div>
    );
  }

  if (field.kind === 'groups') {
    const items = Array.isArray(value) ? value : [];
    const setItem = (i, key, v) => onChange(items.map((it, j) => (j === i ? { ...it, [key]: v } : it)));
    return (
      <div style={{ marginBottom: '12px' }}>
        {header}
        {items.map((item, i) => (
          <div key={i} style={{ border: '1px solid #e0e4e8', borderRadius: '6px', padding: '10px 12px', marginBottom: '8px', background: '#fbfcfd' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <strong style={{ fontSize: '13px', color: '#131843' }}>{groupHeading(field, item, i)}</strong>
              {!disabled && <button type="button" style={smallBtn('#c0392b')} onClick={() => onChange(items.filter((_, j) => j !== i))}>Remove</button>}
            </div>
            {field.item_fields.map(f => (
              <FieldEditor key={f.key} field={f} value={item[f.key]} disabled={disabled}
                onChange={v => setItem(i, f.key, v)} />
            ))}
          </div>
        ))}
        {!disabled && (
          <button type="button" style={smallBtn('#2e6bb5')} onClick={() => onChange([...items, blankItem(field.item_fields)])}>
            + Add {(field.item_label || 'item').toLowerCase()}
          </button>
        )}
      </div>
    );
  }
  return null;
}

export default function ProgramDocumentEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [content, setContent] = useState({});
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [importNote, setImportNote] = useState(null);
  const [preview, setPreview] = useState(null);   // null = closed, {blocks?, error?} = open
  const fileRef = useRef(null);

  const apply = useCallback((d) => {
    setDoc(d); setContent(d.content || {}); setTitle(d.title || ''); setImportNote(null);
  }, []);

  useEffect(() => {
    setDoc(null); setError('');
    axios.get(`${API}/program-documents/${id}`).then(r => apply(r.data)).catch(e => setError(errorText(e)));
  }, [id, apply]);

  const final = doc?.status === 'Final';
  const dirty = !!doc && (isDirty(content, doc.content || {}) || title !== (doc.title || ''));

  // Browsers show their own "leave site?" prompt; the text is ignored but the flag is required.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const run = async (label, fn) => {
    setBusy(label); setError('');
    try { return await fn(); } catch (e) { setError(errorText(e)); return null; } finally { setBusy(''); }
  };

  const save = (extra = {}) => run('save', async () => {
    const r = await axios.put(`${API}/program-documents/${id}`, { title, content, ...extra });
    apply(r.data);
    return r.data;
  });

  const markFinal = async () => {
    const d = await save({ status: 'Final' });
    if (d) setToast({ message: 'Marked Final. Reopen it to make changes.' });
  };
  const reopen = () => run('reopen', async () => {
    const r = await axios.put(`${API}/program-documents/${id}`, { status: 'Draft' });
    apply(r.data);
  });

  const exportAs = async (format) => {
    if (dirty && !final) { const saved = await save(); if (!saved) return; }
    await run(`export-${format}`, () => downloadFileBlob(
      `${API}/program-documents/${id}/export?format=${format}`,
      exportFileName(doc.doc_type, title || 'Program', doc.version, format)));
  };

  // Read an edited Word export back. The server only parses it; the result lands in the editor as
  // unsaved changes so the PM can look it over, and Save is what stores it.
  const importWord = async (file) => {
    if (!file) return;
    if (dirty && !window.confirm('You have unsaved changes. Loading the file replaces them. Continue?')) return;
    await run('import', async () => {
      const fd = new FormData();
      fd.append('file', file, file.name);
      const r = await axios.post(`${API}/program-documents/${id}/import-docx`, fd);
      setContent(r.data.content);
      if (r.data.title) setTitle(r.data.title);
      setImportNote(summarizeImport(file.name, r.data.changed, r.data.warnings));
    });
  };
  const discardImport = () => { setContent(doc.content || {}); setTitle(doc.title || ''); setImportNote(null); };

  // Built by the server from the same blocks the Word and PDF are drawn from, using the editor's
  // current (possibly unsaved) content, so what is shown is what would be exported.
  const openPreview = async () => {
    setPreview({});
    try {
      const r = await axios.post(`${API}/program-documents/${id}/preview`, { title, content });
      setPreview({ blocks: r.data.blocks });
    } catch (e) { setPreview({ error: errorText(e) }); }
  };

  const newVersion = () => run('version', async () => {
    const r = await axios.post(`${API}/program-documents/${id}/new-version`);
    navigate(`/program-documents/${r.data.id}`);
  });

  const remove = async () => {
    if (!window.confirm(`Delete this draft (${DOC_TYPE_LABELS[doc.doc_type]} v${doc.version})? This can't be undone.`)) return;
    const done = await run('delete', async () => { await axios.delete(`${API}/program-documents/${id}`); return true; });
    if (done) navigate('/program-documents');
  };

  if (error && !doc) {
    return (
      <div style={{ padding: '24px' }}>
        <div style={{ color: '#c0392b', marginBottom: '12px' }}>{error}</div>
        <button style={btn('#2c3e50')} onClick={() => navigate('/program-documents')}>← Documents</button>
      </div>
    );
  }
  if (!doc) return <div style={{ padding: '24px', color: '#757575' }}>Loading…</div>;

  const counts = confirmCounts(doc.schema, content, doc.provenance);
  const totalConfirm = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <div style={{ maxWidth: '980px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '10px' }}>
        <span onClick={() => navigate(`/program-documents?program=${doc.program_id}`)}
          style={{ fontSize: '13px', fontWeight: 600, color: '#2e6bb5', cursor: 'pointer' }}>← Documents</span>
        <h2 style={{ margin: 0, color: '#2c3e50', fontSize: '20px' }}>{DOC_TYPE_LABELS[doc.doc_type]}</h2>
        <span style={{ fontSize: '12px', color: '#757575' }}>v{doc.version}</span>
        <span style={{
          fontSize: '11px', fontWeight: 700, padding: '2px 9px', borderRadius: '10px',
          background: final ? '#e6f4ea' : '#fdf2e0', color: final ? '#1e6b43' : '#b9770e',
        }}>{doc.status}</span>
        {dirty && <span style={{ fontSize: '12px', color: '#b9770e' }}>Unsaved changes</span>}
        <div style={{ flexGrow: 1 }} />
        {!final && <button style={btn('#27ae60', !dirty || !!busy)} disabled={!dirty || !!busy} onClick={() => save()}>
          {busy === 'save' ? 'Saving…' : 'Save'}</button>}
        {!final && <button style={btn('#2c3e50', !!busy)} disabled={!!busy} onClick={markFinal}>Mark Final</button>}
        {final && <button style={btn('#2c3e50', !!busy)} disabled={!!busy} onClick={reopen}>Reopen as Draft</button>}
        <button style={btn('#8e44ad', !!busy)} disabled={!!busy} onClick={openPreview}
          title="See the document as it will export, including unsaved changes">Preview doc</button>
        <button style={btn('#2e6bb5', !!busy)} disabled={!!busy} onClick={() => exportAs('docx')}
          title="Download, edit in Word, then upload the file back">
          {busy === 'export-docx' ? 'Preparing…' : 'Word'}</button>
        {!final && (
          <>
            <button style={btn('#2e6bb5', !!busy)} disabled={!!busy} onClick={() => fileRef.current && fileRef.current.click()}
              title="Upload the Word file you downloaded and edited to update this document">
              {busy === 'import' ? 'Reading…' : 'Upload edited Word'}</button>
            <input ref={fileRef} type="file" accept=".docx" style={{ display: 'none' }} aria-label="Edited Word file"
              onChange={e => { const f = e.target.files && e.target.files[0]; e.target.value = ''; importWord(f); }} />
          </>
        )}
        <button style={btn('#2e6bb5', !!busy)} disabled={!!busy} onClick={() => exportAs('pdf')}>
          {busy === 'export-pdf' ? 'Preparing…' : 'PDF'}</button>
        <button style={btn('#7f8c8d', !!busy)} disabled={!!busy} onClick={newVersion}>New version</button>
        {!final && <button style={btn('#c0392b', !!busy)} disabled={!!busy} onClick={remove}>Delete</button>}
      </div>

      {error && <div style={{ background: '#fdecea', border: '1px solid #e6b0aa', color: '#922b21', padding: '8px 12px', borderRadius: '6px', marginBottom: '10px', fontSize: '13px' }}>{error}</div>}

      {importNote && (
        <div style={{ background: '#eaf3fb', border: '1px solid #b7d3ee', borderRadius: '6px', padding: '10px 12px', marginBottom: '10px', fontSize: '13px', color: '#1f4e79' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
            <strong>{importNote.headline}</strong>
            <span onClick={discardImport} style={{ cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}>Discard</span>
          </div>
          {importNote.changed.length > 0 && <ul style={{ margin: '6px 0 0', paddingLeft: '18px' }}>{importNote.changed.map(l => <li key={l}>{l}</li>)}</ul>}
          {importNote.warnings.length > 0 && (
            <div style={{ marginTop: '6px', color: '#b9770e' }}>
              Not placed: {importNote.warnings.map((w, i) => <div key={i}>{w}</div>)}
            </div>
          )}
        </div>
      )}

      <div style={{ marginBottom: '12px', maxWidth: '520px' }}>
        <div style={{ fontSize: '12px', fontWeight: 600, color: '#2c3e50', marginBottom: '3px' }}>Document title</div>
        <input value={title} disabled={final} style={INPUT} onChange={e => setTitle(e.target.value)} />
      </div>

      <div style={{ fontSize: '12px', color: '#5d6d7e', marginBottom: '12px', lineHeight: 1.5 }}>
        {final
          ? 'This document is Final and read-only. Reopen it as a Draft to edit, or make a new version.'
          : 'Filled in from the program, its IO and QA rules. Everything is editable; the chips show where each value came from.'}
        {doc.doc_type === 'work_order' && doc.parent_id && (
          <> Built from <span onClick={() => navigate(`/program-documents/${doc.parent_id}`)}
            style={{ color: '#2e6bb5', cursor: 'pointer', fontWeight: 600 }}>the comms brief</span>. A work order is a snapshot: later changes to the brief or program don't update it.</>
        )}
        {!final && totalConfirm > 0 && <> <strong style={{ color: '#b9770e' }}>{totalConfirm} field{totalConfirm === 1 ? '' : 's'}</strong> still hold suggested wording to confirm.</>}
      </div>

      {doc.schema.sections.map(section => (
        <Section key={section.key} id={`sec-${section.key}`} title={section.title} defaultOpen
          extra={counts[section.key] > 0 && !final
            ? <span style={{ fontSize: '11px', fontWeight: 600, color: '#b9770e', marginRight: '10px' }}>{counts[section.key]} to confirm</span>
            : null}>
          {section.fields.map(field => (
            <FieldEditor key={field.key} field={field} disabled={final}
              value={getValue(content, section.key, field.key)}
              source={((doc.provenance || {})[section.key] || {})[field.key]}
              onChange={v => setContent(c => setValue(c, section.key, field.key, v))} />
          ))}
        </Section>
      ))}

      {preview && (
        <DocumentPreview blocks={preview.blocks} error={preview.error} onClose={() => setPreview(null)}
          footnote={dirty ? 'Includes your unsaved changes.' : 'Matches the saved document.'} />
      )}

      <Toast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}
